import type {
  ReportResult,
  TeamReasonReportResult,
  WeekReasonReportResult,
} from "@hoylu/nexus-service";
import {
  taskTypeDefs,
  taskTypeFill,
  VarianceReasonsColorUtils,
} from "@hoylu/nivo-charts";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useAsyncEffect from "use-async-effect";
import {
  fetchTaskReason,
  fetchTaskReasonByTeam,
  fetchTaskReasonByWeek,
} from "../../state/analytics/analytics.actions";
import {
  selectAnalyticsMasterData,
  selectTaskReason,
  selectTaskReasonByTeam,
  selectTaskReasonByWeek,
} from "../../state/analytics/analytics.selector";
import { Localized } from "../../strings";
import {
  TaskDataBuilder,
  weekGenerator,
} from "../../utils/analytics/task.data.builder";
import { Chart } from "./Chart";
import { FilterContext, toReportFilters } from "./FilterProvider";
import { BarChart, LineChart, PieChart } from "./MemoizedCharts";

export const TaskReason = () => {
  const builder = new TaskDataBuilder();
  const filtersContext = useContext(FilterContext);
  const masterData = useSelector(selectAnalyticsMasterData);

  const strings = Localized.object("ANALYTICS");

  const [reload, setReload] = useState(0);
  const dispatch = useDispatch();
  const reasonData = useSelector(selectTaskReason);
  const reasonDataByWeek = useSelector(selectTaskReasonByWeek);
  const reasonDataByTeam = useSelector(selectTaskReasonByTeam);

  useAsyncEffect(async () => {
    dispatch(
      fetchTaskReason.request({
        ...toReportFilters(filtersContext),
      }),
    );
    dispatch(
      fetchTaskReasonByWeek.request({
        ...toReportFilters(filtersContext),
      }),
    );
    dispatch(
      fetchTaskReasonByTeam.request({
        ...toReportFilters(filtersContext),
      }),
    );
  }, [reload, filtersContext]);

  useEffect(() => {
    setReload(reload + 1);
  }, [filtersContext.project]);

  const pieData = useMemo(() => {
    const collator = Intl.Collator();
    const reasonColorUtils = VarianceReasonsColorUtils.getInstance();
    return builder.buildPieDataSet<ReportResult>(
      reasonData?.data ?? [],
      (reportResult) =>
        masterData.data?.reason.find((r) => r.id === reportResult.value)
          ?.name ?? reportResult.value,
      (reportResult) => reportResult.count,
      (reason) => reasonColorUtils.getColor(reason),
      collator.compare,
    );
  }, [reasonData, masterData]);

  const lineData = useMemo(() => {
    const firstWeek = reasonDataByWeek?.data?.[0]?.week;
    if (!firstWeek) return { data: [], size: 0 };
    const reasonColorUtils = VarianceReasonsColorUtils.getInstance();
    return builder.buildLineDataSet<WeekReasonReportResult, ReportResult>(
      reasonDataByWeek?.data ?? [],
      weekGenerator(firstWeek),
      (reportResult) =>
        masterData.data?.reason?.find((s) => s.id === reportResult.value)
          ?.name ?? reportResult.value,
      (report) => report.week,
      (report) => report.reasons,
      (reportResult) => reportResult.count,
      (reason) => reasonColorUtils.getColor(reason),
      (_a, _b) => 0,
    );
  }, [reasonDataByWeek, masterData]);

  const barData = useMemo(() => {
    const collator = Intl.Collator();
    const reasonColorUtils = VarianceReasonsColorUtils.getInstance();
    return builder.buildBarDataSet<TeamReasonReportResult, ReportResult>(
      reasonDataByTeam?.data ?? [],
      (reportResult) => reportResult.count,
      (report) => report.reasons,
      (report) =>
        masterData.data?.team?.find((t) => t.id === report.team)?.name ??
        report.team,
      (reportResult) =>
        masterData.data?.reason?.find((s) => s.id === reportResult.value)
          ?.name ?? reportResult.value,
      (report) =>
        masterData.data?.team?.find((t) => t.id === report.team)?.shortName ??
        report.team.slice(0, 4),
      (reason) => reasonColorUtils.getColor(reason),
      collator.compare,
      collator.compare,
    );
  }, [reasonDataByTeam, masterData]);

  return (
    <>
      <Chart state={reasonData}>
        <PieChart
          data={pieData}
          defs={taskTypeDefs}
          fill={taskTypeFill}
          showCenterMetric={true}
          suffix={strings.TASKS}
        />
      </Chart>
      <Chart state={reasonDataByWeek}>
        <LineChart
          data={lineData}
          defs={taskTypeDefs}
          fill={taskTypeFill}
          suffix={strings.TASKS}
          yLabel={strings.COUNT}
        />
      </Chart>
      <Chart state={reasonDataByTeam}>
        <BarChart
          data={barData}
          defs={taskTypeDefs}
          fill={taskTypeFill}
          showTooltipTotal={true}
          suffix={strings.TASKS}
          yLabel={strings.COUNT}
        />
      </Chart>
    </>
  );
};
